import { AlertNotification, Button, Dropdown, LabeledCheckbox, LabeledSelect, Loading, Typography } from "@commonsku/styles";
import React, { useEffect, useMemo, useState } from "react";
import useFeatures from "../hooks/useFeatures";
import withReducers from "../store/withReducers";
import { reducers } from "../store/configureManageDistributorApp";
import { paymentModelOptions } from "./dashboard";
import Features from "../constants/Features";
import { toast } from 'react-toastify';
import moment from "moment";
import { isPast } from "../utils";
import Distributors from "../constants/Distributors";
import type { CurrentStripeSubscription } from "../redux/features";

type StripePlan = {
  id: string;
  product_name?: string | null;
  interval?: string | null;
  amount?: number | null;
};

type FeatureWithStripePlans = {
  feature_id: string;
  stripe_plans?: StripePlan[] | null;
  current_stripe_price_id?: string | null;
};

type FeatureOption = {
  label: string;
  value: string;
};

type ManageDistributorStatusProps = {
  distributor: {
    tenant_id: string,
    feature_id: string,
    payment_model: string,
    churning: string | null,
    renewal_date: string,
  };

  onSave: (
    params: {
      paymentModel: string,
      featureId: string,
      syncChangesStripe: boolean,
      stripePriceId?: string | null,
      renewalDate?: string | null,
    }
  ) => Promise<void>;

  onChurn: (
    params: {
      churning: string;
    }
  ) => Promise<void>;

  fetchStripeData?: boolean;
};

const STRIPE_PRODUCT_TO_FEATURE_ID: Record<string, string> = {
  Essentials: Features.DISTRIBUTOR_ESSENTIAL_FEATURE_ID,
  Advanced: Features.DISTRIBUTOR_ADVANCED_FEATURE_ID,
  Enterprise: Features.DISTRIBUTOR_ENTERPRISE_FEATURE_ID,
};

const formatStripePlanLabel = (plan: StripePlan): string => {
  const productName = plan.product_name ?? '';
  const interval = plan.interval
    ? plan.interval.charAt(0).toUpperCase() + plan.interval.slice(1)
    : '';
  const amount = Number(plan.amount ?? 0) / 100;

  return `${productName} / ${interval} ($${amount.toFixed(2)})`;
};

const buildSubscriptionDesync = ({
  features,
  subscription,
  featureId,
  paymentModel,
  actionDate,
  featureOptions,
}: {
  features: FeatureWithStripePlans[];
  subscription: NonNullable<CurrentStripeSubscription>;
  featureId: string;
  paymentModel: string;
  actionDate: string | null;
  featureOptions: FeatureOption[];
}) => {
  const plansByPriceId = new Map<string, StripePlan>();
  let stripeFeature: FeatureWithStripePlans | undefined;

  for (const feature of features) {
    for (const plan of feature.stripe_plans ?? []) {
      plansByPriceId.set(plan.id, plan);
      if (plan.id === subscription.price_id) {
        stripeFeature = feature;
      }
    }
  }

  const stripePlan = plansByPriceId.get(subscription.price_id);
  const databaseFeature = features.find((f) => f.feature_id === featureId);
  const databasePriceId = databaseFeature?.current_stripe_price_id ?? null;
  const databasePlan = databasePriceId ? plansByPriceId.get(databasePriceId) : undefined;
  const productName = subscription.product_name ?? stripePlan?.product_name ?? null;
  const stripeFeatureId = stripeFeature?.feature_id
    ?? (productName ? STRIPE_PRODUCT_TO_FEATURE_ID[productName] : undefined)
    ?? featureId;
  const stripeInterval = subscription.interval?.toUpperCase() ?? null;

  const featureDesynced = stripeFeatureId !== featureId;
  const priceDesynced = subscription.price_id !== databasePriceId;
  const intervalDesynced = null !== stripeInterval && stripeInterval !== paymentModel;

  return {
    stripeFeatureId,
    stripeFeatureLabel: featureOptions.find((f) => f.value === stripeFeatureId)?.label
      ?? productName
      ?? '—',
    databaseFeatureLabel: featureOptions.find((f) => f.value === featureId)?.label ?? '—',
    stripePriceId: subscription.price_id,
    databasePriceId,
    stripePriceLabel: stripePlan ? formatStripePlanLabel(stripePlan) : '—',
    databasePriceLabel: databasePlan ? formatStripePlanLabel(databasePlan) : '—',
    stripeInterval,
    databasePaymentModel: paymentModel,
    databaseActionDate: actionDate,
    featureDesynced,
    priceDesynced,
    intervalDesynced,
    canSyncFeaturePlanInterval: featureDesynced || priceDesynced || intervalDesynced,
    isDesynced: featureDesynced || priceDesynced || intervalDesynced,
  };
};

const ManageDistributorStatus = ({
  distributor,
  onSave,
  onChurn,
}: ManageDistributorStatusProps) => {
  const [featureId, setFeatureId] = useState<string>(distributor.feature_id);
  const [stripePriceId, setStripePriceId] = useState<string | null>(null);
  const [syncChangesStripe, setSyncChangesStripe] = useState<boolean>(false);
  const [features, featuresLoading, currentStripeSubscription, reloadFeatures] = useFeatures(
    'DISTRIBUTOR',
    distributor.tenant_id,
    true,
  );

  const featureDropdownItems = features.filter((f) => ['SOCIAL', 'ESSENTIAL', 'ADVANCED', 'ENTERPRISE'].includes(f.feature_name))
    .map((f) => {
      return {
        label: f.feature_description,
        value: f.feature_id
      };
    });

  const stripePlanOptions = useMemo(() => {
    const feature = features.find((f) => f.feature_id === featureId) as
      | FeatureWithStripePlans
      | undefined;
    const currentInterval = distributor.payment_model?.toLowerCase() ?? '';
    const list = (feature?.stripe_plans ?? []).filter((plan) => {
      if (!currentInterval) {
        return true;
      }
      return (plan.interval ?? '').toLowerCase() === currentInterval;
    });

    return list.map((plan) => ({
      label: formatStripePlanLabel(plan),
      value: plan.id,
    }));
  }, [features, featureId, distributor.payment_model]);

  const currentStripePriceId = useMemo(() => {
    const feature = features.find((f) => f.feature_id === featureId) as
      | FeatureWithStripePlans
      | undefined;
    return feature?.current_stripe_price_id ?? null;
  }, [features, featureId]);

  useEffect(() => {
    if (stripePlanOptions.length === 0) {
      setStripePriceId(null);
      return;
    }

    setStripePriceId((previous) => {
      if (previous && stripePlanOptions.some((option) => option.value === previous)) {
        return previous;
      }
      if (
        currentStripePriceId &&
        stripePlanOptions.some((option) => option.value === currentStripePriceId)
      ) {
        return currentStripePriceId;
      }
      return stripePlanOptions[0].value;
    });
  }, [stripePlanOptions, currentStripePriceId]);

  const confirmAndSave = () => {
    if (syncChangesStripe) {
      if ((distributor.feature_id === Features.DISTRIBUTOR_ADVANCED_FEATURE_ID && featureId === Features.DISTRIBUTOR_ESSENTIAL_FEATURE_ID) ||
        (distributor.feature_id === Features.DISTRIBUTOR_ENTERPRISE_FEATURE_ID && [ Features.DISTRIBUTOR_ESSENTIAL_FEATURE_ID, Features.DISTRIBUTOR_ADVANCED_FEATURE_ID ].includes(featureId))
      ) {
        toast.error('Cannot downgrade Feature level when syncing changes to Stripe');
        setFeatureId(distributor.feature_id);
        return;
      }

      if (featureId === Features.DISTRIBUTOR_ENTERPRISE_FEATURE_ID) {
        toast.error('Cannot upgrade to Enterprise when syncing changes to Stripe');
        setFeatureId(distributor.feature_id);
        return;
      }
    }

    const warnMessage = syncChangesStripe ?
      "You're about to update Stripe's Subscription with the new Feature level. This might trigger invoices immediately. Are you sure?" :
      "You're about to update Feature level without updating Stripe's Subscription. This will not trigger invoices. Are you sure?";

    // eslint-disable-next-line no-restricted-globals
    if (confirm(warnMessage)) {
      onSave({
        featureId,
        paymentModel: distributor.payment_model,
        syncChangesStripe,
        stripePriceId,
      });
    }
  };

  const confirmAndChurn = (immediately: boolean) => {
      let message = immediately ? 'All users will IMMEDIATELY be deactivated, the distributor status will be set to churned and the Stripe subscription will be cancelled. ' : '';
      message += 'Are you sure?';

      // eslint-disable-next-line no-restricted-globals
      if (confirm(message)) {
        const churning = immediately ? Distributors.CHURN_IMMEDIATELY : (
          distributor.churning ? Distributors.CHURN_UNSET : Distributors.CHURN_SET
        );
        onChurn({ churning });
      }
    };

  const checkAndSet = ({ setToFeatureId }: {
    setToFeatureId: string,
  }) => {
    if (setToFeatureId === featureId) {
      return;
    }

    if (distributor.churning) {
      toast.error('Cannot change Feature level on a churning Distributor');
      setFeatureId(distributor.feature_id);
      return;
    }

    if (setToFeatureId === Features.DISTRIBUTOR_SOCIAL_FEATURE_ID) {
      toast.error('Cannot downgrade to Social Feature');
      setFeatureId(distributor.feature_id);
      return;
    }

    setFeatureId(setToFeatureId);
  }

  const syncDatabaseWithStripe = async ({
    featureId: syncFeatureId,
    paymentModel,
    stripePriceId: syncStripePriceId,
    canSyncFeaturePlanInterval,
  }: {
    featureId: string;
    paymentModel: string;
    stripePriceId: string;
    canSyncFeaturePlanInterval: boolean;
  }) => {
    if (!canSyncFeaturePlanInterval) {
      return;
    }
    if (canSyncFeaturePlanInterval && paymentModel) {
      await onSave({
        featureId: syncFeatureId,
        paymentModel,
        syncChangesStripe: false,
        stripePriceId: syncStripePriceId,
      });
      await reloadFeatures();
    }
  };

  const churned = distributor.churning && isPast(distributor.churning);
  const action = churned ? 'Churned' : (
    distributor.churning ? 'Churning' : 'Renewing'
  );

  const actionDate = distributor.churning ?? distributor.renewal_date;

  if ('pending' === featuresLoading) {
    return <Loading mt={200} />;
  }

  const desync = currentStripeSubscription
    ? buildSubscriptionDesync({
        features: features as FeatureWithStripePlans[],
        subscription: currentStripeSubscription,
        featureId,
        paymentModel: distributor.payment_model,
        actionDate,
        featureOptions: featureDropdownItems,
      })
    : null;

  const desyncSummary = desync?.isDesynced ? (
    <>
      <AlertNotification alertType="warn">
        The current subscription is out of sync with the database. Please update the database to match the subscription.
      </AlertNotification>
      <table>
        <thead>
          <tr>
            <th></th>
            <th>On Stripe</th>
            <th>On commonsku</th>
          </tr>
          {desync.featureDesynced && <tr>
            <td>Plan</td>
            <td>{desync.stripeFeatureLabel}</td>
            <td>{desync.databaseFeatureLabel}</td>
          </tr>}
          {desync.priceDesynced && !desync.featureDesynced && <tr>
            <td>Price</td>
            <td>{desync.stripePriceLabel}</td>
            <td>{desync.databasePriceLabel}</td>
          </tr>}
          {desync.intervalDesynced && <tr>
            <td>Interval</td>
            <td>{desync.stripeInterval}</td>
            <td>{desync.databasePaymentModel}</td>
          </tr>}
        </thead>
      </table>
      {desync.isDesynced && <Button onClick={() => syncDatabaseWithStripe({
        featureId: desync.stripeFeatureId,
        paymentModel: desync.stripeInterval as string,
        stripePriceId: desync.stripePriceId,
        canSyncFeaturePlanInterval: desync.canSyncFeaturePlanInterval,
      })}>
        Sync Database with Stripe
      </Button>}
    </>
  ) : null;

  return (
    <>
      {desyncSummary}
      {actionDate && <Typography.H2>{action} on {moment(actionDate).format("MMM Do YYYY")}</Typography.H2>}
      <div style={{marginBottom: 10}}>
        <LabeledSelect
          label="Feature"
          name="featureId"
          value={featureDropdownItems.find((f) => f.value === featureId)}
          options={featureDropdownItems}
          onChange={(e) => checkAndSet({ setToFeatureId: e.value })}
          isClearable={false}
        />
      </div>

      {stripePlanOptions.length > 1 && (
        <div style={{marginBottom: 10}}>
          <LabeledSelect
            label="Stripe price"
            name="stripePriceId"
            value={stripePlanOptions.find((p) => p.value === stripePriceId) ?? null}
            options={stripePlanOptions}
            onChange={(e) => {
              if (e?.value) {
                setStripePriceId(e.value);
              }
            }}
            isClearable={false}
          />
        </div>
      )}

      <div style={{marginBottom: 40}}>
        <LabeledSelect
          label="Payment model"
          name="paymentModel"
          value={paymentModelOptions.find((m) => m.value === distributor.payment_model)}
          options={paymentModelOptions}
          isDisabled={true}
          isClearable={false}
        />
      </div>

      <LabeledCheckbox
        label={'Sync changes to Stripe Subscription'}
        checked={syncChangesStripe}
        onChange={() => setSyncChangesStripe(!syncChangesStripe)}
      />

      <div>
        <Button
          disabled={!!distributor.churning}
          onClick={() => {
            confirmAndSave()
          }}
        >Save</Button>

        {!churned && <Dropdown
          style={{marginLeft: 40}}
          text='Churning status'
          bordered
          items={[
            {
              onClick: () => {
                confirmAndChurn(false);
              },
              content: `${distributor.churning ? 'Unset' : 'Set'} Churning flag`,
            },
            {
              onClick: () => {
                confirmAndChurn(true);
              },
              content: 'Churn immediately'
            },
          ]}
        />}
      </div>
    </>
  );

};

export default withReducers(ManageDistributorStatus, reducers);
